import { useState, useEffect, Dispatch, SetStateAction } from 'react';
import { supabase } from '../supabaseClient';
import type { CurrentUser } from '../types/currentUser';
import type { FetchData } from '../types/appShared';

// ══════════════════════════════════════════════
// useAuthController — نفس فكرة باقي الـ controllers، لكن لصفحة Login/Auth.
// أكتر حساسية من باقي الصفحات لأنها مش ليها View component مستقل (الـ JSX
// بتاعها لسه جوه App.jsx نفسها) وبتتحكم في دخول الفريق كله، فاتنقل هنا
// بحرص شديد وبنفس الأسلوب بالضبط (نفس الأسماء، نفس المنطق، صفر تغييرات في
// أي استدعاء خارجي).
//
// - currentProjectId/setCurrentProjectId, fetchData, loadProjectsFromDatabase
//   اتسابوا في App.jsx (بيتحمّلوا/بيتعدّلوا من أماكن تانية كتير غير الـ Auth)،
//   وبيتبعتوا هنا كـ parameters لأن checkUser/handleVerifyOTP بيستخدموهم.
// - ✅ setShowUserMenu اتشالت من هنا: كانت بتتبعت بس عشان handleVerifyOTP
//   يقفل دروب داون اليوزر (setShowUserMenu(false)) عند تسجيل دخول ناجح —
//   لحظة ده بالتحديد الداشبورد (ومعاها UserMenu) لسه مش موجودة أصلًا، يعني
//   الاستدعاء ده مكانش بيعمل حاجة فعليًا. دلوقتي showUserMenu بقت local state
//   جوه UserMenu.jsx (src/components/common/UserMenu.jsx) بتتصفّر لوحدها
//   default لـ false على أي mount جديد، فمحتاجة مفيش تدخل من هنا خالص.
// - كل الـ ~26 setter بتوع تصفير بيانات المشاريع (setTasksData، setTeamData...
//   إلخ) اتسابوا في App.jsx برضو (بيتحمّلوا من fetchData المركزية) وبيتبعتوا
//   هنا كـ parameters لأن handleLogout لازم يصفّرهم كلهم عند تسجيل الخروج.
//
// resendCount و showWelcome كانوا متعرّفين بعيد عن باقي state الـ Auth في
// App.jsx الأصلي، لكنهم فعليًا خاصين بالـ Auth بس (resendCount بيتغيّر مع كل
// إرسال OTP جديد، showWelcome بيتفعّل بعد تسجيل دخول ناجح) فنقلتهم هنا مع
// باقي state الـ Auth.
//
// ملاحظة TS: الـ ~26 setter بتوع تصفير المشروع بيرجعوا أنواع عناصر مختلفة
// (tasks/team/materials...) ومعظمهم لسه متعرّفين كـ any[] في App.jsx نفسها
// (مكنش فيه وقت نكتب Type كامل لكل دومين قبل كده)، فاتكتبوا هنا كـ
// Dispatch<SetStateAction<any[]>> بدل ما نخترع type غلط ليهم واحد واحد.
// ══════════════════════════════════════════════
type ArraySetter = Dispatch<SetStateAction<any[]>>;

interface LoginForm {
    username: string;
    password: string;
}

// ✅ رسالة تحذير UI بس عند تكرار محاولات الدخول الفاشلة — مش rate limiting
// فعلي من السيرفر (ده متكفّلة بيه Supabase نفسها على مستوى الـ API)، لكن
// طبقة حماية بسيطة وودودة في الواجهة: بعد 5 محاولات باسورد غلط متتالية،
// نوقف المحاولات لدقيقة واحدة ونوضّح للمستخدم السبب بدل ما نسيبه يحاول
// بلا حدود. العداد يترجع للصفر فورًا لو الباسورد صح (حتى لو فشل إرسال الـ
// OTP بعد كده)، فمحدش بيتعاقب على باسورد صحيح.
const LOGIN_ATTEMPT_LIMIT = 5;
const LOGIN_LOCKOUT_MS = 60_000; // دقيقة واحدة

interface UseAuthControllerParams {
    currentProjectId: string | null;
    setCurrentProjectId: Dispatch<SetStateAction<string | null>>;
    fetchData: FetchData;
    loadProjectsFromDatabase: () => Promise<void>;
    setClientComments: ArraySetter;
    setTasksData: ArraySetter;
    setAttendanceData: ArraySetter;
    setDailyActivities: ArraySetter;
    setPhases: ArraySetter;
    setDependencies: ArraySetter;
    setMilestones: ArraySetter;
    setBaselineRevisions: ArraySetter;
    setLeaveRequests: ArraySetter;
    setTaskChallenges: ArraySetter;
    setTeamData: ArraySetter;
    setBudgetData: ArraySetter;
    setMaterialsData: ArraySetter;
    setPerformanceData: ArraySetter;
    setPhotosData: ArraySetter;
    setCommentsData: ArraySetter;
    setCommentReplies: ArraySetter;
    setInvoicesData: ArraySetter;
    setClientAccountData: ArraySetter;
    setDocumentsData: ArraySetter;
    setBudgetCategories: ArraySetter;
    setBudgetExpenses: ArraySetter;
    setRfis: ArraySetter;
    setContacts: ArraySetter;
    setHseIncidents: ArraySetter;
    setAllTeamMembers: ArraySetter;
}

export function useAuthController({
    currentProjectId, setCurrentProjectId, fetchData, loadProjectsFromDatabase,
    setClientComments, setTasksData, setAttendanceData, setDailyActivities,
    setPhases, setDependencies, setMilestones, setBaselineRevisions,
    setLeaveRequests, setTaskChallenges, setTeamData, setBudgetData,
    setMaterialsData, setPerformanceData, setPhotosData, setCommentsData,
    setCommentReplies, setInvoicesData, setClientAccountData, setDocumentsData,
    setBudgetCategories, setBudgetExpenses, setRfis, setContacts,
    setHseIncidents, setAllTeamMembers,
}: UseAuthControllerParams) {
    const [otpCode, setOtpCode] = useState<string[]>(['', '', '', '', '', '', '', '']);
    const [otpTimer, setOtpTimer] = useState(60);
    const [canResend, setCanResend] = useState(false);
    const [step, setStep] = useState('login'); // 'login' or 'otp'
    const [otpError, setOtpError] = useState('');
    const [tempEmail, setTempEmail] = useState('');
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [currentUser, setCurrentUser] = useState<CurrentUser | null>(null);
    const [loginForm, setLoginForm] = useState<LoginForm>({ username: "", password: "" });
    const [loginError, setLoginError] = useState("");
    const [loggingIn, setLoggingIn] = useState(false);
    const [authLoading, setAuthLoading] = useState(true);
    const [resendCount, setResendCount] = useState(0);
    const [showWelcome, setShowWelcome] = useState(false);
    const [failedLoginAttempts, setFailedLoginAttempts] = useState(0);
    const [loginLockoutUntil, setLoginLockoutUntil] = useState<number | null>(null);

    // ✅ أول مشروع نعرضه بعد الدخول (لوجين جديد أو رجوع سيشن قديمة) — لازم
    // يحترم صلاحيات المستخدم، مش يرجع بس لـ localStorage.selectedProject
    // (اللي هي قيمة على مستوى المتصفح مش المستخدم — ممكن تكون فاضلة من آخر
    // شخص استخدم نفس الجهاز)، وإلا مستخدم من غير أي مشروع متعيّن ليه بيشوف
    // بيانات مشروع مش بتاعو. نفس قائمة fullAccess الموجودة في canViewProject.
    const FULL_ACCESS_POSITIONS = ['CEO', 'Program Manager', 'Operations Manager', 'Coordination Team'];
    const resolveStartProject = (profile: any, savedProjectId: string | null): string | null => {
        if (FULL_ACCESS_POSITIONS.includes(profile?.position)) {
            return savedProjectId || 'sec-eoa-jubail';
        }
        const assigned: string[] = profile?.assigned_projects || [];
        if (assigned.length === 0) return null; // ما عندوش أي مشروع متعيّن — ما نحمّل ولا نعرض حاجة
        return savedProjectId && assigned.includes(savedProjectId) ? savedProjectId : assigned[0];
    };

    // ========== CHECK AUTH ON LOAD ==========
    useEffect(() => {
        checkUser();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const checkUser = async () => {
        try {
            const { data: { session } } = await supabase.auth.getSession();

            if (session?.user) {
                const { data: profile } = await supabase
                    .from('user_profiles')
                    .select('*')
                    .eq('user_id', session.user.id)
                    .single();

                if (profile) {
                    setCurrentUser({
                        username: profile.username,
                        name: profile.name,
                        role: profile.role,
                        department: profile.department,
                        position: profile.position,
                        phone: profile.phone,
                        avatar: profile.avatar,
                        email: session.user.email ?? '',
                        assigned_projects: profile.assigned_projects || [],
                    });
                    // ✅ نصحح المشروع المحمّل حسب صلاحيات المستخدم ده تحديدًا،
                    // مش نسيب currentProjectId على قيمة localStorage القديمة
                    const resolvedProject = resolveStartProject(profile, localStorage.getItem('selectedProject'));
                    setCurrentProjectId(resolvedProject);
                    if (resolvedProject) localStorage.setItem('selectedProject', resolvedProject);
                    else localStorage.removeItem('selectedProject');
                    setIsLoggedIn(true);
                } else {
                    // شوف لو في profile بالإيميل بدون user_id
                    const { data: existingProfile } = await supabase
                        .from('user_profiles')
                        .select('*')
                        .eq('email', session.user.email)
                        .is('user_id', null)
                        .single();

                    if (existingProfile) {
                        // ربط الـ user_id تلقائياً
                        await supabase
                            .from('user_profiles')
                            .update({ user_id: session.user.id })
                            .eq('id', existingProfile.id);

                        setCurrentUser({
                            username: existingProfile.username,
                            name: existingProfile.name,
                            role: existingProfile.role,
                            department: existingProfile.department,
                            position: existingProfile.position,
                            phone: existingProfile.phone,
                            avatar: existingProfile.avatar,
                            email: session.user.email ?? '',
                            assigned_projects: existingProfile.assigned_projects || [],
                        });
                        const resolvedProject = resolveStartProject(existingProfile, localStorage.getItem('selectedProject'));
                        setCurrentProjectId(resolvedProject);
                        if (resolvedProject) localStorage.setItem('selectedProject', resolvedProject);
                        else localStorage.removeItem('selectedProject');
                        setIsLoggedIn(true);
                    }
                }
            }
        } catch (error: any) {
            console.error('Error checking auth:', error);
        }
    };

    // ✅ كان فيه هنا قراءة localStorage('currentUser'/'isLoggedIn') كمحاولة
    // استعادة سريعة للسيشن — لكن بعد فحص المشروع كله (grep شامل) لقيت إن محدش
    // بيكتب على المفتاحين دول (localStorage.setItem) في أي مكان، يعني الشرط
    // (savedUser && savedLoginState === 'true') ما كان بيتحقق أبدًا وكان كود
    // ميت 100%. استعادة السيشن الحقيقية بتتم فعليًا من checkUser() (فوق)
    // باستخدام supabase.auth.getSession(). سيبنا بس التايمر اللي بيقفل شاشة
    // تحميل اللوجين الأولية بعد نص ثانية (وضفنا تنظيفه عند الـ unmount).
    useEffect(() => {
        const timer = setTimeout(() => setAuthLoading(false), 500);
        return () => clearTimeout(timer);
    }, []);

    /**
     * دالة تسجيل الدخول
     * تعتمد على مقارنة المدخلات بالبيانات المخزنة في كائن USERS
     * والتي تستمد قيمها الآن من ملف .env.local
     */
    const handleLogin = async () => {
        // ✅ لو لسه في فترة الانتظار من محاولات فاشلة كتير، منوقفش حتى نحاول
        // نتصل بالسيرفر — نوضّح للمستخدم كام ثانية باقية ونرجع فورًا
        if (loginLockoutUntil) {
            if (Date.now() < loginLockoutUntil) {
                const secondsLeft = Math.ceil((loginLockoutUntil - Date.now()) / 1000);
                setLoginError(`Too many failed attempts. Please wait ${secondsLeft} second${secondsLeft === 1 ? '' : 's'} and try again.`);
                return;
            }
            // انتهت فترة الانتظار — رجّع العداد للصفر واسمح بمحاولة جديدة
            setLoginLockoutUntil(null);
            setFailedLoginAttempts(0);
        }

        setLoginError('');
        setLoggingIn(true);

        try {
            let email = loginForm.username;
            if (!email.includes('@')) {
                const emailMap: Record<string, string> = {
                    'bakri': 'b.mohamed@alrammah.com.sa',
                    'khalid': 'k.jehangir@alrammah.com.sa',

                    'naif': 'naif@alrammah.com.sa',
                };
                email = emailMap[email.toLowerCase()] || `${email}@alrammah.com.sa`;
            }

            // ✅ أول حاجة — تحقق إن الإيميل موجود في user_profiles
            const { data: precheck } = await supabase
                .rpc('staff_login_precheck', { p_email: email })
                .single()

            if (!(precheck as any)?.exists_flag) throw new Error('ACCESS_DENIED')
            if ((precheck as any).is_active_flag === false) throw new Error('ACCOUNT_INACTIVE')

            // تحقق من الباسورد
            const { error } = await supabase.auth.signInWithPassword({
                email,
                password: loginForm.password,
            });

            if (error) throw new Error('INVALID_CREDENTIALS')

            // ✅ الباسورد صح — رجّع عداد المحاولات الفاشلة للصفر
            setFailedLoginAttempts(0);

            // باسورد صح — سجل خروج وابعت OTP
            await supabase.auth.signOut();
            const { error: otpError } = await supabase.auth.signInWithOtp({
                email,
                options: { shouldCreateUser: false }
            });
            // ✅ كان بيتجاهل error هنا تمامًا — لو فشل إرسال الـ OTP (مشكلة شبكة
            // مثلاً)، المستخدم كان بيشوف "Invalid username or password" غلط
            // رغم إن الباسورد كان صحيح فعلاً.
            if (otpError) throw new Error('OTP_SEND_FAILED')

            setTempEmail(email);
            setStep('otp');
            setResendCount(prev => prev + 1);
            setOtpTimer(60);
            setCanResend(false);

        } catch (error: any) {
            if (error.message === 'ACCESS_DENIED') {
                setLoginError('Access denied. You are not authorized to access this dashboard.')
                setFailedLoginAttempts(prev => prev + 1);
            } else if (error.message === 'ACCOUNT_INACTIVE') {
                setLoginError('Your account is inactive. Please contact your administrator.')
            } else if (error.message === 'OTP_SEND_FAILED') {
                setLoginError('Your password is correct, but we could not send the verification code. Please check your connection and try again.')
            } else {
                // ✅ باسورد غلط فعليًا — ده الحالة اللي العداد بيتحسب عليها
                setLoginError('Invalid username or password')
                setFailedLoginAttempts(prev => prev + 1);
            }

        } finally {
            setLoggingIn(false);
        }
    };

    // ========== OTP INPUT HANDLERS ==========

    const handleOtpChange = (index: number, value: string) => {
        if (!/^\d*$/.test(value)) return; // أرقام فقط

        const newOtp = [...otpCode];
        newOtp[index] = value.slice(-1); // خانة واحدة بس
        setOtpCode(newOtp);

        // انتقل للخانة التالية تلقائياً
        if (value && index < 7) {
            document.getElementById(`otp-${index + 1}`)?.focus();
        }
    };

    const handleOtpKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Backspace' && !otpCode[index] && index > 0) {
            document.getElementById(`otp-${index - 1}`)?.focus();
        }
    };

    const handleOtpPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
        e.preventDefault();
        const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 8);
        const newOtp = [...otpCode];
        pasted.split('').forEach((char, i) => {
            if (i < 8) newOtp[i] = char;
        });
        setOtpCode(newOtp);
        document.getElementById(`otp-${Math.min(pasted.length, 7)}`)?.focus();
    };

    const handleVerifyOTP = async () => {
        const code = otpCode.join('');
        if (code.length !== 8) return;

        setOtpError('');
        setLoggingIn(true);
        setAuthLoading(true);

        try {
            const { data, error } = await supabase.auth.verifyOtp({
                email: tempEmail,
                token: code,
                type: 'email'
            });

            if (error) throw error;
            if (!data.user) throw new Error('Could not load user profile');

            const { data: profile, error: profileError } = await supabase
                .from('user_profiles')
                .select('*')
                .eq('user_id', data.user.id)
                .single();

            if (profileError || !profile) throw new Error('Could not load user profile');

            setCurrentUser({
                username: profile.username,
                name: profile.name,
                role: profile.role,
                department: profile.department,
                position: profile.position,
                phone: profile.phone,
                avatar: profile.avatar,
                email: data.user.email!,
                assigned_projects: profile.assigned_projects || [],
            });

            setIsLoggedIn(true);
            setShowWelcome(true);
            setLoginForm({ username: '', password: '' });
            setStep('login');
            setOtpCode(['', '', '', '', '', '', '', '']);

            // ✅ كان بياخد آخر مشروع محفوظ في localStorage (أو 'sec-eoa-jubail' كديفولت)
            // من غير ما يتأكد إن المستخدم ده أصلاً مصرّح له بالمشروع ده — ده كان
            // بيخلي مستخدم من غير أي مشروع متعيّن يشوف بيانات مشروع مش بتاعو.
            const resolvedProject = resolveStartProject(profile, localStorage.getItem('selectedProject'));
            setCurrentProjectId(resolvedProject);
            await loadProjectsFromDatabase();
            if (resolvedProject) {
                localStorage.setItem('selectedProject', resolvedProject);
                await fetchData(resolvedProject, true);
            } else {
                localStorage.removeItem('selectedProject');
            }

        } catch (error: any) {
            setOtpError('Invalid or expired code. Please try again.');
            setOtpCode(['', '', '', '', '', '', '', '']);
            document.getElementById('otp-0')?.focus();
            setAuthLoading(false);
        } finally {
            setLoggingIn(false);
            setAuthLoading(false);
        }
    };

    // ========== HANDLE LOGOUT ==========
    const handleLogout = async () => {
        try {
            await supabase.auth.signOut();
            setIsLoggedIn(false);
            setCurrentUser(null);
            setLoginForm({ username: '', password: '' });
            setFailedLoginAttempts(0);
            setLoginLockoutUntil(null);

            // ✅ لازم نصفّر المشروع الحالي وكل بيانات المشروع القديم هنا —
            // وإلا لو مستخدم full-access كان شغال على مشروع (مثلاً Jubail
            // Resident) وعمل logout ودخل مستخدم تاني (محدود الصلاحيات، من
            // غير مشروع متعيّن ليه) في نفس التاب من غير ريفريش كامل للصفحة،
            // البيانات القديمة كانت بتفضل ظاهرة لحد ما useEffect بتاع
            // fetchData يشتغل من جديد — وده كان ممكن يبقى السبب الحقيقي وراء
            // ظهور "مشروع ديفلت وبياناته" لمستخدم مفروض ما يشوف حاجة.
            setCurrentProjectId(null);
            localStorage.removeItem('selectedProject');
            setClientComments([]);
            setTasksData([]);
            setAttendanceData([]);
            setDailyActivities([]);
            setPhases([]);
            setDependencies([]);
            setMilestones([]);
            setBaselineRevisions([]);
            setLeaveRequests([]);
            setTaskChallenges([]);
            setTeamData([]);
            setBudgetData([]);
            setMaterialsData([]);
            setPerformanceData([]);
            setPhotosData([]);
            setCommentsData([]);
            setCommentReplies([]);
            setInvoicesData([]);
            setClientAccountData([]);
            setDocumentsData([]);
            setBudgetCategories([]);
            setBudgetExpenses([]);
            setRfis([]);
            setContacts([]);
            setHseIncidents([]);
            setAllTeamMembers([]);
        } catch (error: any) {
            console.error('Logout error:', error);
        }
    };

    // ✅ وصلنا لحد المحاولات الفاشلة المسموحة — نفعّل فترة الانتظار ونوضّح
    // السبب للمستخدم فورًا (مش لازم ينتظر لحد ما يحاول تاني عشان يشوف الرسالة)
    useEffect(() => {
        if (failedLoginAttempts >= LOGIN_ATTEMPT_LIMIT && !loginLockoutUntil) {
            setLoginLockoutUntil(Date.now() + LOGIN_LOCKOUT_MS);
            setLoginError(`Too many failed attempts. Please wait ${Math.ceil(LOGIN_LOCKOUT_MS / 1000)} seconds and try again.`);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [failedLoginAttempts]);

    useEffect(() => {
        const handleKey = (e: KeyboardEvent) => {
            if (e.key !== 'Enter') return;
            if (step === 'otp') handleVerifyOTP();
            else if (!isLoggedIn) handleLogin();
        };
        window.addEventListener('keydown', handleKey);
        return () => window.removeEventListener('keydown', handleKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- handleLogin/handleVerifyOTP مش memoized؛ الـ listener بيتقرأ من غير مشكلة لأنه بيتسجّل من جديد مع كل تغيير في step/otpCode/loginForm/isLoggedIn أصلًا
    }, [step, otpCode, loginForm, isLoggedIn]);

    useEffect(() => {
        if (step !== 'otp') return;
        setOtpTimer(60);
        setCanResend(false);

        const interval = setInterval(() => {
            setOtpTimer(prev => {
                if (prev <= 1) {
                    clearInterval(interval);
                    setCanResend(true);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(interval);
    }, [step, resendCount]);

    return {
        otpCode, setOtpCode, otpTimer, setOtpTimer, canResend, setCanResend,
        step, setStep, otpError, setOtpError, tempEmail, setTempEmail,
        isLoggedIn, setIsLoggedIn, currentUser, setCurrentUser,
        loginForm, setLoginForm, loginError, setLoginError,
        loggingIn, setLoggingIn, authLoading, setAuthLoading,
        resendCount, setResendCount, showWelcome, setShowWelcome,
        handleLogin, handleOtpChange, handleOtpKeyDown, handleOtpPaste,
        handleVerifyOTP, handleLogout,
    };
}

export default useAuthController;
